import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  formatCurrency,
  formatForFormState,
} from "../../library/utils/numberFormatting";
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Input,
  Spinner,
  Field,
  Dropdown,
  Option,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { ReceiptMoneyRegular } from "@fluentui/react-icons";
import { toast } from "react-toastify";
import { format, parse, isValid } from "date-fns";
import { useCreateInvoice } from "state/queries/useCreateInvoice";
import { ClientParametersDto } from "library/types/types";
import {
  DIFFERENT_PAYER_KEY,
  EMPTY_PAYER_FORM,
  InvoicePayerOption,
  InvoicePayerSection,
  PayerFormData,
  buildPayerFromForm,
  completePayerEmail,
  copyClientAsPayer,
  getPayerValidationError,
} from "components/InvoicePayer";

const CLIENT_PAYER_KEY = "client";

// Styled Components
const StyledDialogSurface = styled(DialogSurface)`
  max-width: 800px;
  width: 90vw;
  max-height: 90vh;
`;

const StyledDialogBody = styled(DialogBody)`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: calc(90vh - 40px);
  overflow: hidden;
`;

const StyledDialogTitle = styled(DialogTitle)`
  flex-shrink: 0;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
`;

const StyledDialogContent = styled(DialogContent)`
  overflow-y: auto !important;
  overflow-x: hidden;
  flex: 1 1 auto;
  min-height: 0;
  padding: 20px 24px;

  /* Ensure scrollbar is visible */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const StyledDialogActions = styled(DialogActions)`
  flex-shrink: 0;
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 16px;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: ${({ theme }) => theme.semanticColors.bodyStandoutBackground};
  border: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 8px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.semanticColors.bodyText};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const FormRowThreeColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 12px;

  .fui-Field {
    min-width: 0;
  }

  .fui-Input,
  input {
    max-width: 120px;
  }
`;

const QuoteInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  font-size: 13px;
`;

const QuoteInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const QuoteInfoLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.semanticColors.bodySubtext};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const QuoteInfoValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.semanticColors.bodyText};
`;

const MoneyInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid
    ${({ theme }) =>
      theme.isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.12)"};
  border-radius: 4px;
  background: ${({ theme }) => theme.semanticColors.inputBackground};

  &:hover {
    border-color: ${({ theme }) =>
      theme.isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.18)"};
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.semanticColors.link};
    box-shadow: 0 0 0 2px
      ${({ theme }) =>
        theme.isDark ? "rgba(56, 169, 255, 0.2)" : "rgba(0, 120, 212, 0.2)"};
  }

  .fui-Input {
    flex: 1;
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    padding-right: 8px;

    &:hover {
      border: none !important;
      background: transparent !important;
    }

    &:focus {
      border: none !important;
      background: transparent !important;
      box-shadow: none !important;
    }

    input {
      text-align: right !important;
    }
  }
`;

const UnitLabel = styled.div`
  padding: 0 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.semanticColors.bodySubtext};
  font-weight: 500;
  white-space: nowrap;
  border-left: 1px solid
    ${({ theme }) =>
      theme.isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.12)"};
  display: flex;
  align-items: center;
  height: 24px;
  background: ${({ theme }) => theme.semanticColors.bodyStandoutBackground};
`;

// Commented out - Installments section removed per requirements
// const InstallmentsTable = styled.div`
//   margin-top: 12px;
// `;

// const InstallmentRow = styled.div`
//   display: grid;
//   grid-template-columns: 80px 1fr 1fr auto;
//   gap: 12px;
//   align-items: center;
//   padding: 8px;
//   background: ${({ theme }) => theme.semanticColors.bodyBackground};
//   border: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
//   border-radius: 4px;
//   margin-bottom: 8px;
// `;

// const InstallmentLabel = styled.span`
//   font-size: 12px;
//   font-weight: 600;
//   color: ${({ theme }) => theme.semanticColors.bodyText};
// `;

// const AddInstallmentButton = styled(Button)`
//   margin-top: 8px;
// `;

// Types
// Commented out - Installments section removed per requirements
// interface InvoiceInstallment {
//   id: string;
//   amount: number;
//   dueDate: string;
// }

// Backend DTO matching C# InvoiceRequestDto (the InvoiceNumber is assigned by the backend)
interface InvoiceRequestDto {
  /** empty = use the invoice number generated by the backend */
  DocumentNumber: string;
  Insurer: string;
  Period: number;
  Payments: number;
  Premium: number;
  Discount: number;
  DiscountPercent: number;
  AnnualPremium?: number; // Optional - only for Casco
  AnnualDiscount?: number; // Optional - only for Casco
  AnnualDiscountPercent?: number; // Optional - only for Casco
  DueDate: string; // ISO date string
  Deductible: string;
  VariantID: number; // Variant index (0 for MTPL, actual variant index for CASCO)
  Payer: ClientParametersDto; // Mandatory: a copy of the client, or a different payer
}

interface CreateInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  offerId: string;
  insuranceLine: "mtpl" | "casco"; // Required - determines which insurance line to invoice
  selectedQuote?: {
    insurer: string;
    variantIndex: number;
    variantID: number;
    premium: number;
    payments: number;
    period?: number; // For MTPL
    deductible?: string; // Deductible from selected Casco variant
    deductibleSecondAccident?: string;
    deductibleTheft?: string;
    deductibleTotalLoss?: string;
    deductibleGlass?: string;
    securitySystem?: string;
    territory?: string;
    transportingExpenses?: string;
    insurerDiscount?: number;
    commissionDiscount?: number;
  };
  isSavedOffer?: boolean; // Whether this is a saved offer or not
  policyNumber?: string; // If policy has been issued
  client?: ClientParametersDto | null; // Client of the offer (Offer.Request.Client), copied as payer for "Same as client"
  onOfferUpdate?: (updatedOffer: any) => void; // Callback to update offer data after invoice creation
}

export const CreateInvoiceDialog = ({
  open,
  onClose,
  offerId,
  insuranceLine,
  selectedQuote,
  isSavedOffer = false,
  policyNumber,
  client,
  onOfferUpdate,
}: CreateInvoiceDialogProps) => {
  const { t } = useTranslation();
  const createInvoiceMutation = useCreateInvoice();

  // Payer (mandatory): "Same as client" copies the client data, otherwise the payer is searched in GOS or entered
  const payerOptions: InvoicePayerOption[] = client
    ? [{ key: CLIENT_PAYER_KEY, source: "client", payer: client }]
    : [];
  const [payerKey, setPayerKey] = useState<string>(
    client ? CLIENT_PAYER_KEY : DIFFERENT_PAYER_KEY,
  );
  const [differentPayer, setDifferentPayer] =
    useState<PayerFormData>(EMPTY_PAYER_FORM);
  // e-mail entered in the dialog when the client has none (the payer e-mail is mandatory)
  const [missingEmail, setMissingEmail] = useState("");

  // every time the dialog opens the payer starts as the client
  useEffect(() => {
    if (!open) return;
    setPayerKey(client ? CLIENT_PAYER_KEY : DIFFERENT_PAYER_KEY);
    setDifferentPayer(EMPTY_PAYER_FORM);
    setMissingEmail("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const getInvoicePayer = (): ClientParametersDto | null => {
    if (payerKey === DIFFERENT_PAYER_KEY) {
      return buildPayerFromForm(differentPayer);
    }
    return client
      ? completePayerEmail(copyClientAsPayer(client), missingEmail)
      : null;
  };

  // The invoice number is assigned by the backend when the invoice is created (Q{yyyyMMdd}-{invoiceId}),
  // so the dialog neither shows nor sends one.

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      // Reset all discount fields
      setDiscount("0.00");
      setDiscountPercent("0.00");
      setAnnualDiscount("0.00");
      setAnnualDiscountPercent("0.00");

      // Reset due date to default
      const date = new Date();
      if (!isSavedOffer) {
        setDueDate(date.toISOString().split("T")[0] || "");
      } else {
        date.setDate(date.getDate() + 15);
        setDueDate(date.toISOString().split("T")[0] || "");
      }
    }
  }, [open, isSavedOffer]);

  // Update values when dialog opens or selectedQuote changes
  useEffect(() => {
    if (open && selectedQuote) {
      if (selectedQuote.insurer) {
        setInsurer(selectedQuote.insurer);
      }
      if (selectedQuote.premium) {
        if (insuranceLine === "casco") {
          // For Casco, set annual premium (quote premium is annual)
          setAnnualPremium(selectedQuote.premium.toString());
        } else {
          // For MTPL, set premium directly
          setPremium(selectedQuote.premium.toString());
        }
      }
      if (insuranceLine === "mtpl" && selectedQuote.period) {
        setPeriod(selectedQuote.period);
      }
      if (insuranceLine === "casco" && selectedQuote.payments) {
        setPayments(selectedQuote.payments);
      }
    }
  }, [open, selectedQuote, insuranceLine]);

  // Document Number - dropdown selector and editable input
  const [documentNumberTemplate, setDocumentNumberTemplate] = useState<string>(
    () => {
      if (isSavedOffer && policyNumber) {
        return "policyNumber";
      }
      return "offerId";
    },
  );

  const [documentNumber, setDocumentNumber] = useState<string>(() => {
    if (isSavedOffer && policyNumber) {
      return policyNumber;
    }
    return offerId;
  });

  const [insurer, setInsurer] = useState(selectedQuote?.insurer || "");

  // Period - for MTPL use selected period, for Casco default to 12
  const [period, setPeriod] = useState(
    insuranceLine === "mtpl" ? selectedQuote?.period || 12 : 12,
  );

  // Payments - for MTPL default to 1, for Casco use selected
  const [payments, setPayments] = useState(
    insuranceLine === "mtpl" ? 1 : selectedQuote?.payments || 1,
  );

  // Annual Premium and Annual Discount - editable fields for Casco
  const [annualPremium, setAnnualPremium] = useState(
    formatForFormState(selectedQuote?.premium) || "",
  );

  const [annualDiscount, setAnnualDiscount] = useState("0.00");
  const [annualDiscountPercent, setAnnualDiscountPercent] = useState("0.00");

  // Premium and Discount - editable fields, initially calculated from annual values
  const [premium, setPremium] = useState(
    formatForFormState(selectedQuote?.premium) || "",
  );

  const [discount, setDiscount] = useState("0.00");
  const [discountPercent, setDiscountPercent] = useState("0.00");

  // Calculate installment premium when annual premium or payments change (for Casco)
  useEffect(() => {
    if (insuranceLine === "casco" && payments > 0) {
      const annualPremiumValue = parseFloat(annualPremium) || 0;
      const installmentPremium = annualPremiumValue / payments;
      setPremium(formatForFormState(installmentPremium));
    }
  }, [annualPremium, payments, insuranceLine]);

  // ============================================================
  // CASCO: Handler for annual premium changes
  // Recalculates discounts based on current percentage
  // ============================================================
  const handleAnnualPremiumChange = (value: string) => {
    setAnnualPremium(value);

    // Recalculate discounts based on current percentage
    const newAnnualPremiumValue = parseFloat(value) || 0;
    const currentPercent = parseFloat(annualDiscountPercent) || 0;

    if (currentPercent > 0) {
      // Calculate new annual discount from new premium and current %
      const newAnnualDiscount = (newAnnualPremiumValue * currentPercent) / 100;
      setAnnualDiscount(formatForFormState(newAnnualDiscount));

      // Calculate new installment premium and discount
      if (payments > 0) {
        const newInstallmentPremium = newAnnualPremiumValue / payments;
        const newInstallmentDiscount =
          (newInstallmentPremium * currentPercent) / 100;
        setDiscount(formatForFormState(newInstallmentDiscount));
      }
    }
  };

  // ============================================================
  // MTPL: Handler for premium changes
  // Recalculates discount based on current percentage
  // ============================================================
  const handleMtplPremiumChange = (value: string) => {
    setPremium(value);

    // Recalculate discount based on current percentage
    const newPremiumValue = parseFloat(value) || 0;
    const currentPercent = parseFloat(discountPercent) || 0;

    if (currentPercent > 0) {
      const newDiscount = (newPremiumValue * currentPercent) / 100;
      setDiscount(formatForFormState(newDiscount));
    }
  };

  // ============================================================
  // CASCO: Handler functions for discount field changes
  // Using direct handlers instead of useEffect to avoid loops
  // ============================================================

  // When Annual Discount % changes
  const handleAnnualDiscountPercentChange = (value: string) => {
    const percentValue = parseFloat(value) || 0;
    const annualPremiumValue = parseFloat(annualPremium) || 0;
    const installmentPremiumValue = parseFloat(premium) || 0;

    setAnnualDiscountPercent(value);

    // Sync percentage to installment
    setDiscountPercent(formatForFormState(percentValue));

    // Calculate annual discount EUR from annual premium
    const calculatedAnnualDiscount = (annualPremiumValue * percentValue) / 100;
    setAnnualDiscount(formatForFormState(calculatedAnnualDiscount));

    // Calculate installment discount EUR from installment premium
    const calculatedInstallmentDiscount =
      (installmentPremiumValue * percentValue) / 100;
    setDiscount(formatForFormState(calculatedInstallmentDiscount));
  };

  // When Annual Discount EUR changes
  const handleAnnualDiscountChange = (value: string) => {
    const annualPremiumValue = parseFloat(annualPremium) || 0;
    const annualDiscountValue = parseFloat(value) || 0;
    const installmentPremiumValue = parseFloat(premium) || 0;

    setAnnualDiscount(value);

    if (annualPremiumValue > 0) {
      // Calculate percentage from annual values
      const calculatedPercent =
        (annualDiscountValue / annualPremiumValue) * 100;
      setAnnualDiscountPercent(formatForFormState(calculatedPercent));

      // Sync percentage to installment
      setDiscountPercent(formatForFormState(calculatedPercent));

      // Calculate installment discount EUR from installment premium
      const calculatedInstallmentDiscount =
        (installmentPremiumValue * calculatedPercent) / 100;
      setDiscount(formatForFormState(calculatedInstallmentDiscount));
    }
  };

  // When Installment Discount % changes
  const handleDiscountPercentChange = (value: string) => {
    const percentValue = parseFloat(value) || 0;
    const annualPremiumValue = parseFloat(annualPremium) || 0;
    const installmentPremiumValue = parseFloat(premium) || 0;

    setDiscountPercent(value);

    // Sync percentage to annual
    setAnnualDiscountPercent(formatForFormState(percentValue));

    // Calculate annual discount EUR from annual premium
    const calculatedAnnualDiscount = (annualPremiumValue * percentValue) / 100;
    setAnnualDiscount(formatForFormState(calculatedAnnualDiscount));

    // Calculate installment discount EUR from installment premium
    const calculatedInstallmentDiscount =
      (installmentPremiumValue * percentValue) / 100;
    setDiscount(formatForFormState(calculatedInstallmentDiscount));
  };

  // When Installment Discount EUR changes
  const handleDiscountChange = (value: string) => {
    const installmentPremiumValue = parseFloat(premium) || 0;
    const installmentDiscountValue = parseFloat(value) || 0;
    const annualPremiumValue = parseFloat(annualPremium) || 0;

    setDiscount(value);

    if (installmentPremiumValue > 0) {
      // Calculate percentage from installment values
      const calculatedPercent =
        (installmentDiscountValue / installmentPremiumValue) * 100;
      setDiscountPercent(formatForFormState(calculatedPercent));

      // Sync percentage to annual
      setAnnualDiscountPercent(formatForFormState(calculatedPercent));

      // Calculate annual discount EUR from annual premium
      const calculatedAnnualDiscount =
        (annualPremiumValue * calculatedPercent) / 100;
      setAnnualDiscount(formatForFormState(calculatedAnnualDiscount));
    }
  };

  // ============================================================
  // MTPL: Handler functions for discount field changes
  // ============================================================

  const handleMtplDiscountPercentChange = (value: string) => {
    const premiumValue = parseFloat(premium) || 0;
    const percentValue = parseFloat(value) || 0;

    setDiscountPercent(value);

    const calculatedDiscount = (premiumValue * percentValue) / 100;
    setDiscount(formatForFormState(calculatedDiscount));
  };

  const handleMtplDiscountChange = (value: string) => {
    const premiumValue = parseFloat(premium) || 0;
    const discountValue = parseFloat(value) || 0;

    setDiscount(value);

    if (premiumValue > 0) {
      const calculatedPercent = (discountValue / premiumValue) * 100;
      setDiscountPercent(formatForFormState(calculatedPercent));
    }
  };

  const [dueDate, setDueDate] = useState(() => {
    const date = new Date();
    if (!isSavedOffer) {
      // If not saved offer, use today
      return date.toISOString().split("T")[0];
    } else {
      // If saved offer, use 15 days from now
      date.setDate(date.getDate() + 15);
      return date.toISOString().split("T")[0];
    }
  });

  // Deductible - hidden, populated from selected Casco variant
  const [deductible] = useState(selectedQuote?.deductible || "");

  const isSubmitting = createInvoiceMutation.isPending;

  // Document number template options for dropdown. "Invoice Number" sends an empty document number:
  // the backend then uses the invoice number it generates.
  const isDocumentNumberFromInvoiceNumber =
    documentNumberTemplate === "invoiceNumber";
  const documentNumberTemplateOptions = [
    { value: "offerId", label: t("qtg.invoice.field.offerId", "Offer ID") },
    {
      value: "invoiceNumber",
      label: t("qtg.invoice.field.invoiceNumber", "Invoice Number"),
    },
  ];

  if (isSavedOffer && policyNumber) {
    documentNumberTemplateOptions.push({
      value: "policyNumber",
      label: t("qtg.invoice.field.policyNumber", "Policy Number"),
    });
  }

  // Handle document number template selection
  const handleDocumentNumberTemplateChange = (template: string) => {
    setDocumentNumberTemplate(template);

    // Pre-populate the document number input based on template selection
    switch (template) {
      case "offerId":
        setDocumentNumber(offerId);
        break;
      case "invoiceNumber":
        // not known yet: the backend fills the document number with the generated invoice number
        setDocumentNumber("");
        break;
      case "policyNumber":
        if (policyNumber) {
          setDocumentNumber(policyNumber);
        }
        break;
      default:
        break;
    }
  };

  const validateForm = (): boolean => {
    if (!documentNumber && !isDocumentNumberFromInvoiceNumber) {
      toast.error(
        t(
          "qtg.invoice.validation.documentNumberRequired",
          "Document number is required",
        ),
      );
      return false;
    }
    if (!insurer) {
      toast.error(
        t("qtg.invoice.validation.insurerRequired", "Insurer is required"),
      );
      return false;
    }
    if (!premium || parseFloat(premium) <= 0) {
      toast.error(
        t(
          "qtg.invoice.validation.premiumRequired",
          "Premium must be greater than zero",
        ),
      );
      return false;
    }
    if (parseFloat(discount) < 0) {
      toast.error(
        t(
          "qtg.invoice.validation.discountInvalid",
          "Discount cannot be negative",
        ),
      );
      return false;
    }
    if (!dueDate) {
      toast.error(
        t("qtg.invoice.validation.dueDateRequired", "Due date is required"),
      );
      return false;
    }

    // Validate due date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(dueDate);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      toast.error(
        t(
          "qtg.invoice.validation.dueDatePast",
          "Due date cannot be in the past",
        ),
      );
      return false;
    }

    const payerError = getPayerValidationError(getInvoicePayer(), t);
    if (payerError) {
      toast.error(payerError);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const requestData: InvoiceRequestDto = {
      DocumentNumber: isDocumentNumberFromInvoiceNumber ? "" : documentNumber,
      Insurer: insurer,
      Period: period,
      Payments: payments,
      Premium: parseFloat(premium),
      Discount: parseFloat(discount),
      DiscountPercent: parseFloat(discountPercent) || 0,
      DueDate: dueDate || new Date().toISOString().split("T")[0] || "",
      Deductible: deductible,
      VariantID: selectedQuote?.variantID ?? 0, // Use actual variant ID from selectedQuote, 0 for MTPL
      Payer: getInvoicePayer() as ClientParametersDto, // validated above
    };

    // Add annual premium and discount for Casco
    if (insuranceLine === "casco") {
      requestData.AnnualPremium = parseFloat(annualPremium) || 0;
      requestData.AnnualDiscount = parseFloat(annualDiscount) || 0;
      requestData.AnnualDiscountPercent =
        parseFloat(annualDiscountPercent) || 0;
    }

    createInvoiceMutation.mutate(
      {
        offerId,
        insuranceLine,
        requestData,
      },
      {
        onSuccess: (updatedOffer) => {
          // Update the offer data with the returned offer object that includes invoice info
          if (onOfferUpdate && updatedOffer) {
            onOfferUpdate(updatedOffer);
          }
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(e, data) => !isSubmitting && onClose()}>
      <StyledDialogSurface>
        <StyledDialogBody>
          <StyledDialogTitle>
            <ReceiptMoneyRegular style={{ fontSize: "20px" }} />
            {t("qtg.invoice.dialog.title", "Create Invoice")}
          </StyledDialogTitle>
          <StyledDialogContent>
            <FormGrid>
              {/* Selected Quote Information - Above Invoice Information */}
              {selectedQuote && (
                <FormSection>
                  <QuoteInfoGrid>
                    <QuoteInfoItem>
                      <QuoteInfoLabel>
                        {t("qtg.invoice.field.insurer", "Insurer")}
                      </QuoteInfoLabel>
                      <QuoteInfoValue>{selectedQuote.insurer}</QuoteInfoValue>
                    </QuoteInfoItem>

                    <QuoteInfoItem>
                      <QuoteInfoLabel>
                        {t("qtg.invoice.field.insuranceLine", "Insurance Line")}
                      </QuoteInfoLabel>
                      <QuoteInfoValue>
                        {insuranceLine === "mtpl" ? "MTPL (OCTA)" : "CASCO"}
                      </QuoteInfoValue>
                    </QuoteInfoItem>

                    <QuoteInfoItem>
                      <QuoteInfoLabel>
                        {t("qtg.invoice.field.variant", "Variant")}
                      </QuoteInfoLabel>
                      <QuoteInfoValue>
                        {selectedQuote.variantIndex + 1}
                      </QuoteInfoValue>
                    </QuoteInfoItem>

                    <QuoteInfoItem>
                      <QuoteInfoLabel>
                        {t("qtg.invoice.field.premium", "Premium")}
                      </QuoteInfoLabel>
                      <QuoteInfoValue>
                        €{formatCurrency(selectedQuote.premium)}
                      </QuoteInfoValue>
                    </QuoteInfoItem>

                    <QuoteInfoItem>
                      <QuoteInfoLabel>
                        {t("qtg.invoice.field.payments", "Payments")}
                      </QuoteInfoLabel>
                      <QuoteInfoValue>{selectedQuote.payments}</QuoteInfoValue>
                    </QuoteInfoItem>

                    {selectedQuote.period && (
                      <QuoteInfoItem>
                        <QuoteInfoLabel>
                          {t("qtg.invoice.field.period", "Period")}
                        </QuoteInfoLabel>
                        <QuoteInfoValue>
                          {selectedQuote.period}{" "}
                          {t("qtg.invoice.field.months", "months")}
                        </QuoteInfoValue>
                      </QuoteInfoItem>
                    )}

                    {selectedQuote.deductible && (
                      <QuoteInfoItem>
                        <QuoteInfoLabel>
                          {t("qtg.invoice.field.deductible", "Deductible")}
                        </QuoteInfoLabel>
                        <QuoteInfoValue>
                          {selectedQuote.deductible}
                        </QuoteInfoValue>
                      </QuoteInfoItem>
                    )}

                    {selectedQuote.deductibleSecondAccident && (
                      <QuoteInfoItem>
                        <QuoteInfoLabel>
                          {t(
                            "qtg.invoice.field.deductibleSecondAccident",
                            "2nd Accident Deductible",
                          )}
                        </QuoteInfoLabel>
                        <QuoteInfoValue>
                          {selectedQuote.deductibleSecondAccident}
                        </QuoteInfoValue>
                      </QuoteInfoItem>
                    )}

                    {selectedQuote.deductibleTheft && (
                      <QuoteInfoItem>
                        <QuoteInfoLabel>
                          {t(
                            "qtg.invoice.field.deductibleTheft",
                            "Theft Deductible",
                          )}
                        </QuoteInfoLabel>
                        <QuoteInfoValue>
                          {selectedQuote.deductibleTheft}
                        </QuoteInfoValue>
                      </QuoteInfoItem>
                    )}

                    {selectedQuote.deductibleTotalLoss && (
                      <QuoteInfoItem>
                        <QuoteInfoLabel>
                          {t(
                            "qtg.invoice.field.deductibleTotalLoss",
                            "Total Loss Deductible",
                          )}
                        </QuoteInfoLabel>
                        <QuoteInfoValue>
                          {selectedQuote.deductibleTotalLoss}
                        </QuoteInfoValue>
                      </QuoteInfoItem>
                    )}

                    {selectedQuote.deductibleGlass && (
                      <QuoteInfoItem>
                        <QuoteInfoLabel>
                          {t(
                            "qtg.invoice.field.deductibleGlass",
                            "Glass Deductible",
                          )}
                        </QuoteInfoLabel>
                        <QuoteInfoValue>
                          {selectedQuote.deductibleGlass}
                        </QuoteInfoValue>
                      </QuoteInfoItem>
                    )}

                    {selectedQuote.securitySystem && (
                      <QuoteInfoItem>
                        <QuoteInfoLabel>
                          {t(
                            "qtg.invoice.field.securitySystem",
                            "Security System",
                          )}
                        </QuoteInfoLabel>
                        <QuoteInfoValue>
                          {selectedQuote.securitySystem}
                        </QuoteInfoValue>
                      </QuoteInfoItem>
                    )}

                    {selectedQuote.territory && (
                      <QuoteInfoItem>
                        <QuoteInfoLabel>
                          {t("qtg.invoice.field.territory", "Territory")}
                        </QuoteInfoLabel>
                        <QuoteInfoValue>
                          {selectedQuote.territory}
                        </QuoteInfoValue>
                      </QuoteInfoItem>
                    )}

                    {selectedQuote.transportingExpenses && (
                      <QuoteInfoItem>
                        <QuoteInfoLabel>
                          {t(
                            "qtg.invoice.field.transportingExpenses",
                            "Transporting Expenses",
                          )}
                        </QuoteInfoLabel>
                        <QuoteInfoValue>
                          {selectedQuote.transportingExpenses}
                        </QuoteInfoValue>
                      </QuoteInfoItem>
                    )}

                    {selectedQuote.insurerDiscount !== undefined &&
                      selectedQuote.insurerDiscount > 0 && (
                        <QuoteInfoItem>
                          <QuoteInfoLabel>
                            {t(
                              "qtg.invoice.field.insurerDiscount",
                              "Insurer Discount",
                            )}
                          </QuoteInfoLabel>
                          <QuoteInfoValue>
                            €{formatCurrency(selectedQuote.insurerDiscount)}
                          </QuoteInfoValue>
                        </QuoteInfoItem>
                      )}

                    {selectedQuote.commissionDiscount !== undefined &&
                      selectedQuote.commissionDiscount > 0 && (
                        <QuoteInfoItem>
                          <QuoteInfoLabel>
                            {t(
                              "qtg.invoice.field.commissionDiscount",
                              "Commission Discount",
                            )}
                          </QuoteInfoLabel>
                          <QuoteInfoValue>
                            €{formatCurrency(selectedQuote.commissionDiscount)}
                          </QuoteInfoValue>
                        </QuoteInfoItem>
                      )}
                  </QuoteInfoGrid>
                </FormSection>
              )}

              {/* Invoice Information */}
              <FormSection>
                <SectionTitle>
                  {t("qtg.invoice.section.invoiceInfo", "Invoice Information")}
                </SectionTitle>

                <FormRow>
                  {/* Due Date - Editable, cannot be in past */}
                  <Field data-help="offer-invoice-due-date"
                    label={t("qtg.invoice.field.dueDate", "Due Date")}
                    required
                    size="small"
                  >
                    <DatePicker
                      value={
                        dueDate
                          ? parse(dueDate, "yyyy-MM-dd", new Date())
                          : null
                      }
                      onSelectDate={(date: Date | null | undefined) => {
                        if (date && isValid(date)) {
                          // Convert Date to ISO format (YYYY-MM-DD) for form state
                          const isoDate = format(date, "yyyy-MM-dd");
                          setDueDate(isoDate);
                        } else {
                          setDueDate("");
                        }
                      }}
                      formatDate={(date?: Date) => {
                        // Format display as DD.MM.YYYY (Latvian format)
                        return date && isValid(date)
                          ? format(date, "dd.MM.yyyy")
                          : "";
                      }}
                      placeholder="DD.MM.YYYY"
                      style={{ width: "150px" }}
                      minDate={new Date()}
                      size="small"
                      disabled={isSubmitting}
                    />
                  </Field>
                </FormRow>

                {/* Document Number - Dropdown to select template + Editable Input in same row */}
                <FormRow>
                  <Field
                    label={t(
                      "qtg.invoice.field.documentNumberTemplate",
                      "Document Number Template",
                    )}
                    size="small"
                  >
                    <Dropdown
                      value={
                        documentNumberTemplateOptions.find(
                          (opt) => opt.value === documentNumberTemplate,
                        )?.label || ""
                      }
                      selectedOptions={[documentNumberTemplate]}
                      onOptionSelect={(e, data) => {
                        if (data.optionValue) {
                          handleDocumentNumberTemplateChange(data.optionValue);
                        }
                      }}
                      size="small"
                      disabled={isSubmitting}
                    >
                      {documentNumberTemplateOptions.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>

                  <Field data-help="offer-invoice-doc-number"
                    label={t(
                      "qtg.invoice.field.documentNumber",
                      "Document Number",
                    )}
                    required
                    size="small"
                  >
                    <Input
                      value={documentNumber}
                      onChange={(e) => setDocumentNumber(e.target.value)}
                      placeholder={
                        isDocumentNumberFromInvoiceNumber
                          ? t(
                              "qtg.invoice.field.documentNumberFromInvoiceNumber",
                              "Set to the invoice number assigned when the invoice is created",
                            )
                          : t(
                              "qtg.invoice.field.documentNumberPlaceholder",
                              "Enter or modify document number",
                            )
                      }
                      size="small"
                      disabled={isSubmitting || isDocumentNumberFromInvoiceNumber}
                    />
                  </Field>
                </FormRow>

                {/* Insurer - Hidden/Read-only */}
                <Field
                  label={t("qtg.invoice.field.insurer", "Insurer")}
                  style={{ display: "none" }}
                >
                  <Input value={insurer} readOnly disabled />
                </Field>

                {/* Period - Hidden for display but included in request */}
                <Field
                  label={t("qtg.invoice.field.period", "Period (months)")}
                  style={{ display: "none" }}
                >
                  <Input value={period.toString()} readOnly disabled />
                </Field>

                {/* Payments - Hidden for display but included in request */}
                <Field
                  label={t("qtg.invoice.field.payments", "Payments")}
                  style={{ display: "none" }}
                >
                  <Input value={payments.toString()} readOnly disabled />
                </Field>

                {/* Annual Premium and Annual Discount - Only for Casco */}
                {insuranceLine === "casco" && (
                  <FormRowThreeColumns>
                    {/* Annual Premium - Editable with EUR suffix */}
                    <Field
                      label={t(
                        "qtg.invoice.field.annualPremium",
                        "Annual Premium",
                      )}
                      required
                      size="small"
                    >
                      <MoneyInputContainer>
                        <Input
                          type="number"
                          step="0.01"
                          value={annualPremium}
                          onChange={(e) =>
                            handleAnnualPremiumChange(e.target.value)
                          }
                          size="small"
                          disabled={isSubmitting}
                        />
                        <UnitLabel>EUR</UnitLabel>
                      </MoneyInputContainer>
                    </Field>

                    {/* Annual Discount % */}
                    <Field
                      label={t(
                        "qtg.invoice.field.annualDiscountPercent",
                        "Discount %",
                      )}
                      size="small"
                    >
                      <MoneyInputContainer>
                        <Input
                          type="number"
                          step="0.01"
                          value={annualDiscountPercent}
                          onChange={(e) =>
                            handleAnnualDiscountPercentChange(e.target.value)
                          }
                          size="small"
                          disabled={isSubmitting}
                        />
                        <UnitLabel>&nbsp;%&nbsp;&nbsp;</UnitLabel>
                      </MoneyInputContainer>
                    </Field>

                    {/* Annual Discount EUR */}
                    <Field
                      label={t("qtg.invoice.field.annualDiscount", "Discount")}
                      size="small"
                    >
                      <MoneyInputContainer>
                        <Input
                          type="number"
                          step="0.01"
                          value={annualDiscount}
                          onChange={(e) =>
                            handleAnnualDiscountChange(e.target.value)
                          }
                          size="small"
                          disabled={isSubmitting}
                        />
                        <UnitLabel>EUR</UnitLabel>
                      </MoneyInputContainer>
                    </Field>

                    {/* Annual Total - Read-only calculated field */}
                    <Field
                      label={t("qtg.invoice.field.annualTotal", "Total")}
                      size="small"
                    >
                      <MoneyInputContainer>
                        <Input
                          type="text"
                          value={formatCurrency(
                            (parseFloat(annualPremium) || 0) -
                              (parseFloat(annualDiscount) || 0),
                          )}
                          size="small"
                          readOnly
                          disabled
                        />
                        <UnitLabel>EUR</UnitLabel>
                      </MoneyInputContainer>
                    </Field>
                  </FormRowThreeColumns>
                )}

                <FormRowThreeColumns>
                  {/* Premium - Editable for both MTPL and Casco */}
                  <Field data-help="offer-invoice-premium"
                    label={t(
                      insuranceLine === "casco"
                        ? "qtg.invoice.field.installmentPremium"
                        : "qtg.invoice.field.premium",
                      insuranceLine === "casco"
                        ? "Installment Premium"
                        : "Premium",
                    )}
                    required
                    size="small"
                  >
                    <MoneyInputContainer>
                      <Input
                        type="number"
                        step="0.01"
                        value={premium}
                        onChange={(e) =>
                          insuranceLine === "mtpl"
                            ? handleMtplPremiumChange(e.target.value)
                            : setPremium(e.target.value)
                        }
                        size="small"
                        disabled={isSubmitting}
                      />
                      <UnitLabel>EUR</UnitLabel>
                    </MoneyInputContainer>
                  </Field>

                  {/* Discount % */}
                  <Field
                    label={t(
                      insuranceLine === "casco"
                        ? "qtg.invoice.field.installmentDiscountPercent"
                        : "qtg.invoice.field.discountPercent",
                      "Discount %",
                    )}
                    size="small"
                  >
                    <MoneyInputContainer>
                      <Input
                        type="number"
                        step="0.01"
                        value={discountPercent}
                        onChange={(e) =>
                          insuranceLine === "casco"
                            ? handleDiscountPercentChange(e.target.value)
                            : handleMtplDiscountPercentChange(e.target.value)
                        }
                        size="small"
                        disabled={isSubmitting}
                      />
                      <UnitLabel>&nbsp;%&nbsp;&nbsp;</UnitLabel>
                    </MoneyInputContainer>
                  </Field>

                  {/* Discount EUR */}
                  <Field
                    label={t(
                      insuranceLine === "casco"
                        ? "qtg.invoice.field.installmentDiscount"
                        : "qtg.invoice.field.discount",
                      "Discount",
                    )}
                    size="small"
                  >
                    <MoneyInputContainer>
                      <Input
                        type="number"
                        step="0.01"
                        value={discount}
                        onChange={(e) =>
                          insuranceLine === "casco"
                            ? handleDiscountChange(e.target.value)
                            : handleMtplDiscountChange(e.target.value)
                        }
                        size="small"
                        disabled={isSubmitting}
                      />
                      <UnitLabel>EUR</UnitLabel>
                    </MoneyInputContainer>
                  </Field>

                  {/* Total Invoice Premium - Read-only calculated field */}
                  <Field
                    label={t(
                      insuranceLine === "casco"
                        ? "qtg.invoice.field.totalInstallmentPremium"
                        : "qtg.invoice.field.totalPremium",
                      "Total",
                    )}
                    size="small"
                  >
                    <MoneyInputContainer>
                      <Input
                        type="text"
                        value={formatCurrency(
                          (parseFloat(premium) || 0) -
                            (parseFloat(discount) || 0),
                        )}
                        size="small"
                        readOnly
                        disabled
                      />
                      <UnitLabel>EUR</UnitLabel>
                    </MoneyInputContainer>
                  </Field>
                </FormRowThreeColumns>

                {/* Deductible - Hidden, value from Casco variant */}
                <Field
                  label={t("qtg.invoice.field.deductible", "Deductible")}
                  style={{ display: "none" }}
                >
                  <Input value={deductible} readOnly disabled />
                </Field>
              </FormSection>

              {/* Payer - the party the invoice is addressed to */}
              <InvoicePayerSection
                dataHelp="offer-invoice-payer"
                options={payerOptions}
                selectedKey={payerKey}
                onSelectedKeyChange={setPayerKey}
                differentPayer={differentPayer}
                onDifferentPayerChange={setDifferentPayer}
                missingEmail={missingEmail}
                onMissingEmailChange={setMissingEmail}
                disabled={isSubmitting}
              />

              {/* Commented out - Installments section removed per requirements */}
              {/* 
              {installments.length > 0 && (
                <FormSection>
                  <SectionTitle>
                    {t("qtg.invoice.section.installments", "Installments")} (
                    {installments.length})
                  </SectionTitle>
                  <InstallmentsTable>
                    {installments.map((installment, index) => (
                      <InstallmentRow key={installment.id}>
                        <InstallmentLabel>#{index + 1}</InstallmentLabel>
                        <Field
                          label={t("qtg.invoice.field.amount", "Amount (€)")}
                          size="small"
                        >
                          <Input
                            type="number"
                            step="0.01"
                            value={installment.amount.toString()}
                            onChange={(e) =>
                              handleInstallmentChange(
                                installment.id,
                                "amount",
                                parseFloat(e.target.value) || 0
                              )
                            }
                            size="small"
                          />
                        </Field>
                        <Field
                          label={t("qtg.invoice.field.dueDate", "Due Date")}
                          size="small"
                        >
                          <Input
                            type="date"
                            value={installment.dueDate}
                            onChange={(e) =>
                              handleInstallmentChange(
                                installment.id,
                                "dueDate",
                                e.target.value
                              )
                            }
                            size="small"
                          />
                        </Field>
                        <Button
                          appearance="subtle"
                          size="small"
                          onClick={() =>
                            handleRemoveInstallment(installment.id)
                          }
                        >
                          {t("qtg.invoice.button.remove", "Remove")}
                        </Button>
                      </InstallmentRow>
                    ))}
                  </InstallmentsTable>
                  <AddInstallmentButton
                    appearance="secondary"
                    size="small"
                    onClick={handleAddInstallment}
                  >
                    {t("qtg.invoice.button.addInstallment", "Add Installment")}
                  </AddInstallmentButton>
                </FormSection>
              )}

              {installments.length === 0 && (
                <AddInstallmentButton
                  appearance="secondary"
                  size="small"
                  onClick={handleAddInstallment}
                >
                  {t(
                    "qtg.invoice.button.addFirstInstallment",
                    "Add Installments"
                  )}
                </AddInstallmentButton>
              )}
              */}
            </FormGrid>
          </StyledDialogContent>
          <StyledDialogActions>
            <Button
              appearance="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("qtg.invoice.button.cancel", "Cancel")}
            </Button>
            <Button
              data-help="offer-invoice-create"
              appearance="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner size="tiny" style={{ marginRight: "8px" }} />
                  {t("qtg.invoice.button.creating", "Creating...")}
                </>
              ) : (
                t("qtg.invoice.button.create", "Create Invoice")
              )}
            </Button>
          </StyledDialogActions>
        </StyledDialogBody>
      </StyledDialogSurface>
    </Dialog>
  );
};

export default CreateInvoiceDialog;
